<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto relative">
    <!-- Table Header with Summary -->
    <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
      <div class="space-y-4">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

            <h3 class="text-lg font-semibold text-gray-900">
              Packages
            </h3>
          </div>
        </div>

        <!-- Summary Row -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex justify-between gap-4">
            <div class="text-center md:text-left">
              <p class="text-sm text-gray-500">
                Packages
              </p>

              <p class="text-lg font-semibold text-gray-900">
                {{ packageRows.length }}
              </p>
            </div>

            <div class="text-center md:text-right ml-auto">
              <p class="text-sm text-gray-500">
                Claims This Month
              </p>

              <div class="flex items-center justify-end space-x-2">
                <div
                  v-if="subcontractorSummary.draft > 0"
                  class="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.draft }} Draft
                </div>

                <div
                  v-if="subcontractorSummary.draft_subcontractor > 0"
                  class="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.draft_subcontractor }} Issued
                </div>

                <div
                  v-if="subcontractorSummary.submitted > 0"
                  class="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.submitted }} Submitted
                </div>

                <div
                  v-if="subcontractorSummary.certified > 0"
                  class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                >
                  {{ subcontractorSummary.certified }} Certified
                </div>

                <span>
                  {{ packagesWithClaim }}/{{ packageRows.length }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Packages List -->
    <div
      class="relative overflow-x-auto"
      :class="{ 'opacity-50': isLoading }"
    >
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50 border-b border-gray-200">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Package
            </th>

            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Scope Gap
            </th>

            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Approved Variation
            </th>

            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Final Account
            </th>

            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Actual Forecast
            </th>

            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Final Forecast
            </th>

            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" />
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-100 bg-white">
          <ExpandablePackageRow
            v-for="row in packageRows"
            :key="row.package.id"
            :subcontractor-package="row.package"
            :project-supplier="row.projectSupplier"
            :project-id="projectId"
            :lite-mode="liteMode"
            @create-claim="onCreateClaim"
            @issue-claim="emit('issue-claim', $event)"
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { getNextTimeBarSortKey } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { useSuppliersStore } from '@/store/supplier';
import { DateTime } from 'luxon';
import { computed, watch } from 'vue';
import ExpandablePackageRow from './ExpandablePackageRow.vue';

const props = defineProps<{
  projectId: string;
}>();

const emit = defineEmits<{
  (e: 'create-claim', data: { projectSupplier: ProjectSupplier; packageData: Package; latestClaim?: Claim }): void;
  (e: 'issue-claim', data: { claim: Claim; email: string }): void;
}>();

const packagesStore = usePackagesStore();
const suppliersStore = useSuppliersStore();
const projectsStore = useProjectsStore();
const project = computed(() => projectsStore.project);
const fetchDashboardAction = useStoreApiAction(packagesStore.fetchSubcontractorDashboard);
const currentMonth = computed(() => DateTime.now().toFormat('yyyy-MM'));
const currentMonthAsDate = computed(() => DateTime.now().startOf('month'));

watch(() => project.value?.id, (projectId) => {
  if(projectId) {
    fetchDashboardAction.request(projectId, {
      month: currentMonth.value,
    });
  }
}, { immediate: true });

const packages = computed(() => {
  return packagesStore.models
    .where('project_id', Number(props.projectId))
    .with('subcontractor', (query) => {
      query.with('projects', (query) => {
        query.where('project_id', Number(props.projectId))
          .with('supplier');
      });
    })
    .with('package_claims', (query) => {
      query.with('claim');
    })
    .get();
});

const getClaimForMonth = (subcontractorPackage: Package) => {
  return subcontractorPackage.package_claims?.filter((packageClaim) => {
    return packageClaim.claim?.start >= currentMonthAsDate.value.startOf('month').toISO() &&
      packageClaim.claim?.start <= currentMonthAsDate.value.endOf('month').toISO();
  })[0]?.claim;
};

const packageRows = computed(() => {
  return packages.value
    .map((pkg) => ({
      package: pkg,
      projectSupplier: pkg.subcontractor?.projects?.[0],
    }))
    .filter((row) => !!row.projectSupplier);
});

const packagesWithClaim = computed(() => {
  return packages.value.filter((pkg) => {
    return getClaimForMonth(pkg) !== undefined;
  }).length;
});

const subcontractorSummary = computed(() => {
  const summary = { draft: 0, draft_subcontractor: 0, submitted: 0, certified: 0, noClaim: 0, total: 0 };

  packageRows.value.forEach((pkg) => {
    const latestClaim = getClaimForMonth(pkg.package);

    if(!latestClaim) {
      summary.noClaim++;
    } else if(latestClaim.status === ClaimStatus.draft) {
      summary.draft++;
      summary.total++;
    } else if(latestClaim.status === ClaimStatus.draft_subcontractor) {
      summary.draft_subcontractor++;
      summary.total++;
    } else if(latestClaim.status === ClaimStatus.subcontractor_claim) {
      summary.submitted++;
      summary.total++;
    } else if(latestClaim.status === ClaimStatus.certified) {
      summary.certified++;
      summary.total++;
    }
  });

  return summary;
});

const liteMode = computed(() => !!project.value?.feature_flag_subcontractor_claims_lite);
const isLoading = computed(() => fetchDashboardAction.is(States.LOADING));

const onCreateClaim = (data: { projectSupplier: ProjectSupplier; packageData: Package; latestClaim?: Claim }) => {
  emit('create-claim', data);
};
</script>

<style scoped></style>
